import { format } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Wordmark } from '@fhs/ui';
import { Box, HStack, ScrollView, Text, View } from '@fhs-legacy/universal-components';
import { IServerOrder } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import QRCode from 'components/qrcode';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useIsMobileBp } from 'hooks/breakpoints';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useDialogModal from 'hooks/use-dialog-modal';
import useOrderNumber from 'hooks/use-order-number';
import { FutureOrderTimeCard } from 'pages/cart/future-order-time-card';
import { PickupPin, QRContainer, QRWrapper } from 'pages/order-confirmation/common';
import OrderIssues from 'pages/order-confirmation/order-issues';
import PickupCard from 'pages/order-confirmation/pickup-card';
import { PrepareNowButton } from 'pages/order-confirmation/prepare-now';
import { SubheadingText } from 'pages/order-confirmation/subheading-text';
import { useAuthContext } from 'state/auth';
import { ServiceMode } from 'state/order';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { focusableLabeledA11yProps } from 'utils/accessibility/focusable-labeled-props';
import { fullBrandName } from 'utils/environment';
import { useFlag } from 'utils/launchdarkly';
import { checkIfDateIsWithinCloseTimeAndMinutes, readableCloseHourToday } from 'utils/restaurant';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';

import {
  ActionButtonWrapper,
  Container,
  FutureOrderTimeCardWrapper,
  Header,
  InfoLine,
  InfoWrapper,
  LogoContainer,
} from './styled';

const qrCodeOptions = {
  margin: 1,
  scale: 4,
  color: {
    dark: primitive.$black,
    light: primitive.$white,
  },
};

function PickupConfirmed({ serverOrder }: { serverOrder: IServerOrder }) {
  const { user } = useAuthContext();
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const { formatMessage } = useIntl();
  const { store } = useStoreContext();
  const isMobile = useIsMobileBp();
  const isShowingOrderingIssue = useFlag('show-ordering-issue-info');
  const enableClosingTimeDialog = useFlag('enable-closing-time-dialog');

  const { pickupDate } = useOrderTimedFire({ serverOrder });
  const serverOrderServiceMode = serverOrder.cart.serviceMode;

  const isPickupServiceMode =
    serverOrderServiceMode === ServiceMode.TAKEOUT ||
    serverOrderServiceMode === ServiceMode.DRIVE_THRU;
  const showPickupClosingTimeDialog =
    enableClosingTimeDialog &&
    isPickupServiceMode &&
    !!store.driveThruHours &&
    checkIfDateIsWithinCloseTimeAndMinutes(store.driveThruHours, new Date(Date.now()), 60);

  const [PickupClosingTimeDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Store closing hour dialog',
    init: showPickupClosingTimeDialog,
  });

  // String.fromCharCode(160) puts a non-breaking space in to prevent "10:03" and "pm" from breaking onto different lines in this heading
  const pickupTime = format(pickupDate, 'h:mm aaaa').replace(' ', String.fromCharCode(160));

  const orderNumber = useOrderNumber(serverOrder);

  const name = (user && user.details.name) || '';

  const { fulfillmentDetails } = serverOrder;

  const getOrderModeText = (orderNum: string | null) => {
    switch (serverOrderServiceMode) {
      case ServiceMode.PICKUP_WINDOW:
        return formatMessage({ id: 'orderScheduledForRapidRescuePickUpLane' });
      case ServiceMode.EAT_IN:
        return formatMessage(
          { id: 'orderScheduledForDineIn' },
          {
            orderNumber: orderNum ? `#${orderNum}` : '',
            linebreak: '\n',
          }
        );
      default:
        return formatMessage(
          { id: 'orderScheduledForPickup' },
          {
            orderNumber: orderNum ? `#${orderNum}` : '',
            linebreak: '\n',
          }
        );
    }
  };

  const orderNumberText = formatMessage({
    id: 'orderNumber',
  });

  const pickupOrderAlert = formatMessage(
    { id: 'pickUpOrderSuccessfulAlert' },
    {
      orderNumber,
      brand: fullBrandName(),
      address: `${serverOrder.cart.storeAddress.addressLine1} ${serverOrder.cart.storeAddress.addressLine2}`,
    }
  );

  const pickupHeadingText = getOrderModeText(orderNumber);
  const orderNumberAnnouncement =
    orderNumber && orderNumberText ? `${orderNumberText}: ${orderNumber}` : '';

  return (
    <SafeAreaView edges={Platform.OS === 'android' ? ['bottom'] : []} style={{ flex: 1 }}>
      <View px={{ base: '$4', desktop: '$0' }} justifyContent="space-between" gap="$4" flex={1}>
        <ScrollView flex={{ base: 1, desktop: 0 }}>
          <Container>
            {isMobile && (
              <LogoContainer>
                <Wordmark />
              </LogoContainer>
            )}
            <Box {...focusableLabeledA11yProps(pickupHeadingText)}>
              <Header importantForAccessibility="no-hide-descendants" testID="order-preparation-confirmation">
                {pickupHeadingText}
              </Header>
            </Box>

            {!!fulfillmentDetails?.pickupQrCode && (
              <QRContainer>
                <QRWrapper>
                  <QRCode
                    barcode={fulfillmentDetails.pickupQrCode}
                    options={qrCodeOptions}
                    testID="qr-code"
                  />
                </QRWrapper>
              </QRContainer>
            )}
            <SubheadingText
              name={name}
              pickupTime={pickupTime}
              serviceMode={serverOrderServiceMode}
            />
            {!!fulfillmentDetails?.pickupPin && (
              <PickupPin>{fulfillmentDetails.pickupPin}</PickupPin>
            )}
            <InfoWrapper
              {...(orderNumberAnnouncement
                ? focusableLabeledA11yProps(orderNumberAnnouncement)
                : {})}
            >
              {!!orderNumber && (
                <HStack importantForAccessibility="no-hide-descendants">
                  <InfoLine testID="order-number" mr="$1" fontWeight="bold">
                    {orderNumberText}:
                  </InfoLine>
                  <Text>{orderNumber}</Text>
                </HStack>
              )}
            </InfoWrapper>
            <PrepareNowButton serverOrder={serverOrder} />
            <FutureOrderTimeCardWrapper>
              <FutureOrderTimeCard
                date={{
                  year: pickupDate.getFullYear(),
                  month: pickupDate.getMonth(),
                  day: pickupDate.getDate(),
                  hour: pickupDate.getHours(),
                  minutes: pickupDate.getMinutes(),
                }}
                readOnly
              />
            </FutureOrderTimeCardWrapper>
            <PickupCard confirmed serverOrder={serverOrder} />
          </Container>
          {isShowingOrderingIssue && <OrderIssues />}

          <VisuallyHidden role="alert" accessibilityLabel={pickupOrderAlert} />

          <PickupClosingTimeDialog
            testID="store-closing-hour-dialog"
            heading={formatMessage({ id: 'orderPickup' })}
            body={formatMessage(
              { id: 'dontForgetToPickYourOrder' },
              { time: readableCloseHourToday(store.driveThruHours, timeFormatConfig) }
            )}
          />
        </ScrollView>

        <ActionButtonWrapper>
          <ActionButton
            fullWidth
            padding="$2"
            to={`/receipt/${serverOrder.rbiOrderId}`}
            accessibilityRole="button"
            accessibilityLabel={formatMessage({ id: 'viewReceipt', description: '' })}
            _text={{ numberOfLines: 1 }}
          >
            {formatMessage({ id: 'viewReceipt', description: '' })}
          </ActionButton>
        </ActionButtonWrapper>
      </View>
    </SafeAreaView>
  );
}

export default React.memo(PickupConfirmed);
